import Button from "@/components/forms/Button/Button";
import React, { useState } from "react";
import { TeamMember, useUserContext } from "@/contexts/UserContext";
import styles from "./team.module.css";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import { AssistantPermissions, AssistantStatus, UserRole } from "@boxem/core-api-schema";
import IconEditGoogle from "@/components/icons/IconEditGoogle";
import { deleteMember, inviteMember } from "@/api/teams";
import IconDelete from "@/components/icons/iconDelete";
import IconResend from "@/components/icons/IconResend";
import IconUserProfile from "@/components/icons/IconUserProfile";
import MemberInviteModal from "@/components/MemberInviteModal/MemberInviteModal";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import SellerAccountBanner from "@/components/SellerAccountBanner/SellerAccountBanner";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import PageContainer from "../container/PageContainer";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Grid } from "@/components/Grid/Grid";
import Flex from "@/components/Flex/Flex";

export default function SettingsTeamPage() {
  const { seller, user, team, setTeam , teamLoading} = useUserContext();

  const { showNotification } = useNotificationContext();
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [currentMember, setCurrentMember] = useState<TeamMember>(null);
  const [currentMemberIndex, setCurrentMemberIndex] = useState(-1);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<string>("");

  const handleInvite = () => {
    setCurrentMember(null);
    setInviteOpen(true);
  };

  const handleEdit = (member: TeamMember) => {
    setCurrentMember(member);
    setInviteOpen(true);
  };

  const handleResend = (member: TeamMember) => {
    setResendLoading(member.uuid);
    inviteMember(seller.uuid, member).then(() => {
      showNotification("Invite message sent!", NotificationType.SUCCESS);
    }).catch(err => {
      showNotification( "Failed to resend invite message. Please try again later.", NotificationType.ERROR);
    }).finally(() => setResendLoading(""));
  }; 

  const handleDelete = (member: TeamMember, index: number) => {
    setCurrentMember(member);
    setCurrentMemberIndex(index);
    setConfirmModalOpen(true);
  };

  return (
    <PageContainer>
      <ConfirmModal
        open={confirmModalOpen}
        body={<p style={{margin:"20px", padding:"20px"}}>Are you sure you want to delete this user?</p>} 
        onClose={(confirmed) => {
          if (confirmed && currentMember) {
            // delete the member
            deleteMember(currentMember).then(() => {
                showNotification("Member successfully deleted!", NotificationType.SUCCESS);
                setTeam(team.filter((_, index) => index !== currentMemberIndex));
            })
          } 
          setConfirmModalOpen(false);
        }}
      />
      <MemberInviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        member={currentMember}
        team={team} 
        setTeam={(_team) => {
          setTeam(_team);
        }}
      />

      <div className={styles.teamSettingsContainer}>
        <SellerAccountBanner />

        <div className={styles.teamHeader}>Users</div>

        <div className={styles.teamContainer}>
          <div className={styles.teamLabelContainer}>
            <div className={styles.teamLabelHeaderText}>
              People in your Boxem account
            </div>
            <div className={styles.teamLabelSubText}>
              Manage people and their permissions within your workspace.
            </div>
            <Tooltip
              title={user.role !== UserRole.SELLER ? "Only the seller can invite assistants" : (!user.sellerId || user.sellerId === "") ? "A connected seller account is required before inviting assistants" : ""}
            >
              <Button
                type="primary"
                disabled={user.role !== UserRole.SELLER || (!user.sellerId || user.sellerId === "")}
                className={styles.inviteButton}
                onClick={handleInvite}
              >
                Invite people
              </Button>
            </Tooltip>
           
          </div>
          <div className={styles.teamControlsContainer}>
            {(team.length === 0 || teamLoading) && (
              <div className={styles.userZeroState}>
                <div className={styles.userZeroStateContentContainer}>
                  {teamLoading ? (<LoadingSpinner size="3rem" />) : (
                    <>
                      <div className={styles.zeroStateIconContainer}>
                        <IconUserProfile size={"3rem"} />
                      </div>
                      <div className={styles.zeroStateHeader}>You don't have any users yet</div>
                      <Tooltip
                        title={user.role !== UserRole.SELLER ? "Only the seller can invite assistants" : (!user.sellerId || user.sellerId === "") ? "A connected seller account is required before inviting assistants" : ""}
                      >
                      <Button
                          type="primary"
                          disabled={user.role !== UserRole.SELLER || (!user.sellerId || user.sellerId === "")}
                          onClick={handleInvite}
                        >
                          Invite people
                        </Button>
                      </Tooltip>
 
                    </>
                  )}
                </div>
              </div>
            )}
            {(team.length > 0 && !teamLoading) && team.map((member, index) => {
              const last = index === team.length - 1;
              const createShipmentEnabled = ((member.permissions & AssistantPermissions.CREATE_SHIPMENTS_PERMISSION) > 0);
              const manageShipmentEnabled = ((member.permissions & AssistantPermissions.MANAGE_SHIPMENT_PERMISSION) > 0);
              const dashboardEnabled = ((member.permissions & AssistantPermissions.DASHBOARD_PERMISSION) > 0);
              const inventoryEnabled = ((member.permissions & AssistantPermissions.INVENTORY_PERMISSION) > 0);

              const getReadableStatus = (status: AssistantStatus) => {
                switch (status) {
                  case AssistantStatus.ACTIVE:
                    return "Active";
                  case AssistantStatus.PENDING:
                    return "Pending";
                  case AssistantStatus.REMOVED:
                    return "Removed";
                  default:
                    return "Pending";
                }
              };

              return (
                <div key={index} className={`${styles.teamMemberContainer} ${!last ? styles.teamMemberBorder : ""}`}>
                  <div className={styles.memberInfoContainer}>
                    <div className={styles.memberName}>{member.name}</div>
                    <div className={styles.memberEmail}>{member.email}</div>
                  </div>
                  <div className={`
                    ${styles.memberStatusContainer}
                    ${member.status === AssistantStatus.ACTIVE ? styles.memberActive : ""}
                    ${(member.status === AssistantStatus.PENDING || !member.status) ? styles.memberPending : ""}
                    ${member.status === AssistantStatus.REMOVED ? styles.memberRemoved : ""}
                  `}>
                    {getReadableStatus(member.status)}
                  </div>
                  <Grid templateColumns="1fr 1fr" gap="0.5rem">
                    {dashboardEnabled && (
                      <Flex gap={2} className={styles.permissionContainer}>
                        <IconCheckCircle />
                        <div className={styles.permissionText}>Dashboard</div>
                      </Flex>
                    )}
                    {createShipmentEnabled && (
                      <Flex gap={2} className={styles.permissionContainer}>
                        <IconCheckCircle />
                        <div className={styles.permissionText}>Create shipment</div>
                      </Flex>
                    )}
                    {inventoryEnabled && (
                      <Flex gap={2} className={styles.permissionContainer}>
                        <IconCheckCircle />
                        <div className={styles.permissionText}>Inventory</div>
                      </Flex>
                    )}
                    {manageShipmentEnabled && (
                      <Flex gap={2} className={styles.permissionContainer}>
                        <IconCheckCircle />
                        <div className={styles.permissionText}>Manage shipment</div>
                      </Flex>
                    )}
                  </Grid>
                  <div className={styles.buttonsContainer}>
                    <Button
                      type="text"
                      disabled={user?.role === UserRole.ASSISTANT}
                      className={styles.button}
                      onClick={() => handleEdit(member)}
                    >
                      <Tooltip title={"Edit permissions"}>
                        <IconEditGoogle size={25} />
                      </Tooltip>
                    </Button>

                    <Button
                      type="text"
                      disabled={user?.role === UserRole.ASSISTANT}
                      className={styles.button}
                      onClick={() => handleResend(member)}
                    >
                      {resendLoading === member.uuid ? (<LoadingSpinner size="1rem" className={styles.loader} />) : (
                        <Tooltip title={"Resend invite"}>
                          <IconResend size={25} />
                        </Tooltip>
                      )}
                    </Button>

                    <Button
                      type="text"
                      className={styles.button}
                      disabled={user?.role === UserRole.ASSISTANT}
                      onClick={() => handleDelete(member, index)}
                    >
                      <Tooltip title={"Delete"}>
                        <IconDelete size={25} />
                      </Tooltip>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
